/**
 * GDPR Service
 * 
 * Frontend service for GDPR compliance features including data export and account deletion.
 * Handles secure API communication and user interaction flows.
 */

import { AuthCore } from '../core/AuthCore';

export interface DataExportRequest {
  format?: 'json' | 'csv' | 'both';
  includeFiles?: boolean;
}

export interface DataExportResponse {
  message: string;
  downloadUrl: string;
  expiresAt: string;
  note: string;
}

export interface AccountDeletionInitRequest {
  reason?: string;
}

export interface AccountDeletionInitResponse {
  message: string;
  confirmationRequired: boolean;
  note: string;
  expiresAt: string;
}

export interface AccountDeletionConfirmRequest {
  confirmationCode: string;
  reason?: string;
}

export interface AccountDeletionConfirmResponse {
  message: string;
  note: string;
  deletedAt: string;
}

export interface GDPRStatusResponse {
  accountStatus: string;
  deletedAt?: string;
  deletionReason?: string;
  recentExports: Array<{
    exportedAt: string;
    expiresAt?: string;
  }>;
  pendingDeletion?: {
    initiatedAt: string;
    expiresAt: string;
  };
  canRequestExport: boolean;
}

class GDPRService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_URL || 'https://2ixm2n2j2bfc4nuezpzqdxkgym0uhsdq.lambda-url.us-east-1.on.aws';
  }

  /**
   * Get authentication headers
   */
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const authCore = AuthCore.getInstance();
    const token = await authCore.getAccessToken();

    if (!token) {
      throw new Error('Authentication required');
    }

    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  /**
   * Request data export
   */
  async requestDataExport(request: DataExportRequest = {}): Promise<DataExportResponse> {
    try {
      const headers = await this.getAuthHeaders();

      const response = await fetch(`${this.baseUrl}/user/export-data`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          format: request.format || 'json',
          includeFiles: request.includeFiles || false
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        if (response.status === 429) {
          throw new Error(`Rate limit exceeded. ${errorData.error?.message || 'Please try again later.'}`);
        }
        
        throw new Error(errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Data export request failed:', error);
      throw error;
    }
  }

  /**
   * Get GDPR status
   */
  async getGDPRStatus(): Promise<GDPRStatusResponse> {
    try {
      const headers = await this.getAuthHeaders();

      const response = await fetch(`${this.baseUrl}/user/gdpr-status`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('GDPR status check failed:', error);
      throw error;
    }
  }

  /**
   * Initiate account deletion
   */
  async initiateAccountDeletion(request: AccountDeletionInitRequest = {}): Promise<AccountDeletionInitResponse> {
    try {
      const headers = await this.getAuthHeaders();

      const response = await fetch(`${this.baseUrl}/user/delete-account/initiate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          reason: request.reason
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Account deletion initiation failed:', error);
      throw error;
    }
  }

  /**
   * Confirm account deletion
   */
  async confirmAccountDeletion(request: AccountDeletionConfirmRequest): Promise<AccountDeletionConfirmResponse> {
    try {
      const headers = await this.getAuthHeaders();

      const response = await fetch(`${this.baseUrl}/user/delete-account/confirm`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          confirmationCode: request.confirmationCode,
          reason: request.reason
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Account deletion confirmation failed:', error);
      throw error;
    }
  }

  /**
   * Download data export file
   */
  async downloadExportFile(downloadUrl: string, filename?: string): Promise<void> {
    try {
      let blob: Blob;
      
      // Check if it's a data URL (for testing)
      if (downloadUrl.startsWith('data:')) {
        // Handle data URL directly without fetch (CSP bypass)
        const base64Data = downloadUrl.split(',')[1];
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        blob = new Blob([bytes], { type: 'application/zip' });
      } else {
        // Handle regular HTTP URLs
        const response = await fetch(downloadUrl);
        
        if (!response.ok) {
          throw new Error(`Download failed: ${response.status} ${response.statusText}`);
        }
        
        blob = await response.blob();
      }

      const url = window.URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename || `aimetapic-data-export-${new Date().toISOString().split('T')[0]}.zip`;
      
      document.body.appendChild(a);
      a.click();
      
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('File download failed:', error);
      throw error;
    }
  }

  /**
   * Format export date for display
   */
  formatExportDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Calculate time until expiration
   */
  getTimeUntilExpiration(expiresAt: string): string {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffMs = expiry.getTime() - now.getTime();
    
    if (diffMs <= 0) {
      return 'Expired';
    }
    
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} remaining`;
    } else {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} remaining`;
    }
  }
}

export const gdprService = new GDPRService();
